"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  accuracyBand,
  type FluencyResult,
  fluency,
  NORM_PERCENTILES,
  type NormRow,
  ORF_NORMS_2017,
  percentileReached,
  type Season,
} from "@/lib/tool-kit/grades/wcpm";

/** English strings. A translated content file overrides these through `ui`. */
export const WCPM_CALCULATOR_STRINGS = {
  wordsLabel: "Words read (all words attempted)",
  errorsLabel: "Errors",
  minutesLabel: "Minutes",
  secondsLabel: "Seconds",
  gradeLabel: "Grade",
  grades: {
    "1": "Grade 1",
    "2": "Grade 2",
    "3": "Grade 3",
    "4": "Grade 4",
    "5": "Grade 5",
    "6": "Grade 6",
  },
  seasonLabel: "Time of year",
  seasons: {
    fall: "Fall",
    winter: "Winter",
    spring: "Spring",
  },
  bandsHeading: "Accuracy bands",
  independentLabel: "Independent at or above (%)",
  instructionalLabel: "Instructional at or above (%)",
  bandsHint:
    "Defaults are the running-record bands: easy 96–100%, instructional 90–95%, hard below 90%. Change them to match your program.",
  results: "Result",
  wcpm: "Words correct per minute",
  accuracy: "Accuracy",
  wordsCorrect: "Words correct",
  level: "Text level for this reader",
  bands: {
    independent: "Independent",
    instructional: "Instructional",
    frustration: "Frustration (hard)",
  },
  working: "({words} − {errors}) × 60 ÷ {seconds} s = {wcpm} WCPM",
  normsHeading: "Hasbrouck & Tindal (2017) norms: {grade}, {season}",
  colPercentile: "Percentile",
  colWcpm: "WCPM",
  percentile: "{p}th",
  placement: "At or above the {p}th percentile.",
  belowTenth: "Below the 10th percentile.",
  vsMedianAbove: "{diff} WCPM above the 50th percentile ({median}).",
  vsMedianBelow: "{diff} WCPM below the 50th percentile ({median}).",
  vsMedianAt: "Exactly at the 50th percentile ({median}).",
  guidance:
    "Hasbrouck and Tindal suggest that a reader 10 or more WCPM below the 50th percentile — averaged over two unpracticed grade-level passages — needs fluency-building instruction.",
  noNorms:
    "The 2017 norms have no fall benchmark for grade 1. Choose winter or spring.",
  invalid:
    "Enter the words read, the errors (no more than the words read) and a reading time above zero.",
  invalidBands:
    "Set both cutoffs from 0 to 100, with the independent cutoff at or above the instructional one.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "{wcpm} WCPM, {accuracy}% accuracy ({band})",
} as const;

type Strings = typeof WCPM_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type GradeKey = keyof Strings["grades"];

function readSeconds(minutesRaw: string, secondsRaw: string): number | null {
  const minutes = minutesRaw.trim() === "" ? 0 : parseNumberInput(minutesRaw);
  const seconds = secondsRaw.trim() === "" ? 0 : parseNumberInput(secondsRaw);
  if (minutes === null || seconds === null || minutes < 0 || seconds < 0) {
    return null;
  }
  return minutes * 60 + seconds;
}

function NormsComparison({
  wcpm,
  norms,
  heading,
  strings,
  fmt,
}: {
  wcpm: number;
  norms: NormRow;
  heading: string;
  strings: Strings;
  fmt: Format;
}) {
  const reached = percentileReached(wcpm, norms);
  const median = norms[50];
  const diff = wcpm - median;
  const diffText = fmt(Math.abs(diff), { maximumFractionDigits: 1 });
  let medianLine = t(strings.vsMedianAt, { median: fmt(median) });
  if (diff > 0) {
    medianLine = t(strings.vsMedianAbove, {
      diff: diffText,
      median: fmt(median),
    });
  } else if (diff < 0) {
    medianLine = t(strings.vsMedianBelow, {
      diff: diffText,
      median: fmt(median),
    });
  }
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{heading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.colPercentile}
              </th>
              <th className={TH} scope="col">
                {strings.colWcpm}
              </th>
            </tr>
          </thead>
          <tbody>
            {NORM_PERCENTILES.map((p) => (
              <tr
                className={
                  p === reached ? "bg-primary/10 font-semibold" : undefined
                }
                key={p}
              >
                <td className={TD}>{t(strings.percentile, { p })}</td>
                <td className={TD}>{fmt(norms[p])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-foreground text-sm">
        {reached === null
          ? strings.belowTenth
          : t(strings.placement, { p: reached })}{" "}
        {medianLine}
      </p>
      {diff <= -10 ? <Notice>{strings.guidance}</Notice> : null}
    </section>
  );
}

function ResultPanel({
  result,
  seconds,
  wordsRead,
  errors,
  band,
  strings,
  fmt,
}: {
  result: FluencyResult;
  seconds: number;
  wordsRead: number;
  errors: number;
  band: string | null;
  strings: Strings;
  fmt: Format;
}) {
  const wcpm = fmt(result.wcpm, { maximumFractionDigits: 1 });
  const accuracy = fmt(result.accuracy, { maximumFractionDigits: 1 });
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.wcpm} value={wcpm} />
        <StatCard label={strings.accuracy} value={`${accuracy}%`} />
        <StatCard label={strings.wordsCorrect} value={fmt(result.correct)} />
        <StatCard label={strings.level} value={band ?? "—"} />
      </StatGrid>
      {band === null ? <Notice>{strings.invalidBands}</Notice> : null}
      <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm">
        {t(strings.working, {
          words: fmt(wordsRead),
          errors: fmt(errors),
          seconds: fmt(seconds, { maximumFractionDigits: 1 }),
          wcpm,
        })}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={t(strings.summary, { wcpm, accuracy, band: band ?? "—" })}
      />
    </>
  );
}

export default function WcpmCalculatorWidget() {
  const strings = useToolUi(WCPM_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [wordsRaw, setWordsRaw] = useState("152");
  const [errorsRaw, setErrorsRaw] = useState("8");
  const [minutesRaw, setMinutesRaw] = useState("1");
  const [secondsRaw, setSecondsRaw] = useState("30");
  const [grade, setGrade] = useState<GradeKey>("3");
  const [season, setSeason] = useState<Season>("winter");
  const [independentRaw, setIndependentRaw] = useState("96");
  const [instructionalRaw, setInstructionalRaw] = useState("90");

  const wordsRead = parseNumberInput(wordsRaw);
  const errors = errorsRaw.trim() === "" ? 0 : parseNumberInput(errorsRaw);
  const seconds = readSeconds(minutesRaw, secondsRaw);
  const result =
    wordsRead === null || errors === null || seconds === null
      ? null
      : fluency(wordsRead, errors, seconds);
  const independent = parseNumberInput(independentRaw);
  const instructional = parseNumberInput(instructionalRaw);
  const bandsValid =
    independent !== null &&
    instructional !== null &&
    instructional >= 0 &&
    independent <= 100 &&
    independent >= instructional;
  const band =
    result && bandsValid
      ? strings.bands[accuracyBand(result.accuracy, independent, instructional)]
      : null;
  const norms = ORF_NORMS_2017[Number(grade)]?.[season] ?? null;

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="wcpm-words"
            label={strings.wordsLabel}
            numeric
            onChange={setWordsRaw}
            value={wordsRaw}
          />
          <TextField
            id="wcpm-errors"
            label={strings.errorsLabel}
            numeric
            onChange={setErrorsRaw}
            value={errorsRaw}
          />
          <TextField
            id="wcpm-minutes"
            label={strings.minutesLabel}
            numeric
            onChange={setMinutesRaw}
            value={minutesRaw}
          />
          <TextField
            id="wcpm-seconds"
            label={strings.secondsLabel}
            numeric
            onChange={setSecondsRaw}
            value={secondsRaw}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="wcpm-grade"
            label={strings.gradeLabel}
            onChange={setGrade}
            options={(Object.keys(strings.grades) as GradeKey[]).map((key) => ({
              value: key,
              label: strings.grades[key],
            }))}
            value={grade}
          />
          <SelectField
            id="wcpm-season"
            label={strings.seasonLabel}
            onChange={setSeason}
            options={(["fall", "winter", "spring"] as const).map((key) => ({
              value: key,
              label: strings.seasons[key],
            }))}
            value={season}
          />
        </div>
      </div>

      <div className={`${CARD} space-y-4`}>
        <h3 className={WIDGET_H3}>{strings.bandsHeading}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="wcpm-independent"
            label={strings.independentLabel}
            numeric
            onChange={setIndependentRaw}
            value={independentRaw}
          />
          <TextField
            id="wcpm-instructional"
            label={strings.instructionalLabel}
            numeric
            onChange={setInstructionalRaw}
            value={instructionalRaw}
          />
        </div>
        <p className="text-muted-foreground text-xs">{strings.bandsHint}</p>
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {result === null ||
        wordsRead === null ||
        errors === null ||
        seconds === null ? (
          <Notice>{strings.invalid}</Notice>
        ) : (
          <ResultPanel
            band={band}
            errors={errors}
            fmt={fmt}
            result={result}
            seconds={seconds}
            strings={strings}
            wordsRead={wordsRead}
          />
        )}
      </section>

      {result && norms ? (
        <NormsComparison
          fmt={fmt}
          heading={t(strings.normsHeading, {
            grade: strings.grades[grade],
            season: strings.seasons[season],
          })}
          norms={norms}
          strings={strings}
          wcpm={result.wcpm}
        />
      ) : null}
      {norms === null ? <Notice>{strings.noNorms}</Notice> : null}
    </div>
  );
}
